import { formatCurrency, formatPercent } from "../data/mock/dashboardData";

export function getInitialFilters(config) {
  const initial = { search: "" };
  for (const field of config.fields ?? []) {
    switch (field.type) {
      case "multi":
        initial[field.key] = [];
        break;
      case "range":
        initial[field.key] = { min: "", max: "" };
        break;
      case "dateRange":
        initial[field.key] = { from: "", to: "" };
        break;
      default:
        initial[field.key] = "";
    }
  }
  return initial;
}

function getFieldValue(row, field) {
  if (field.getValue) return field.getValue(row);
  return row[field.key];
}

function isRangeActive(range) {
  return range && (range.min !== "" || range.max !== "");
}

function isDateRangeActive(range) {
  return range && (range.from !== "" || range.to !== "");
}

export function countActiveFilters(filters, config) {
  let count = 0;
  if (filters.search?.trim()) count += 1;

  for (const field of config.fields ?? []) {
    const val = filters[field.key];
    switch (field.type) {
      case "multi":
        if (val?.length) count += 1;
        break;
      case "range":
        if (isRangeActive(val)) count += 1;
        break;
      case "dateRange":
        if (isDateRangeActive(val)) count += 1;
        break;
      default:
        if (val !== "" && val != null) count += 1;
    }
  }
  return count;
}

export function getUniqueValues(data, key, getValue) {
  const values = new Set();
  for (const row of data) {
    const v = getValue ? getValue(row) : row[key];
    if (v != null && v !== "" && v !== "—") values.add(v);
  }
  return [...values].sort((a, b) => String(a).localeCompare(String(b)));
}

export function buildFieldOptions(data, fields) {
  const options = {};
  for (const field of fields ?? []) {
    if (field.options) {
      options[field.key] = field.options;
    } else if (field.optionsFrom) {
      options[field.key] = getUniqueValues(data, field.optionsFrom, field.getValue);
    }
  }
  return options;
}

export function applyTableFilters(data, filters, config) {
  let result = data;

  if (filters.search?.trim()) {
    const q = filters.search.trim().toLowerCase();
    const keys = config.searchKeys ?? [];
    result = result.filter((row) =>
      keys.some((k) => String(row[k] ?? "").toLowerCase().includes(q))
    );
  }

  for (const field of config.fields ?? []) {
    const val = filters[field.key];
    if (val == null) continue;

    switch (field.type) {
      case "multi":
        if (!val.length) break;
        result = result.filter((row) => val.includes(getFieldValue(row, field)));
        break;
      case "select":
        if (val === "") break;
        result = result.filter((row) => String(getFieldValue(row, field)) === String(val));
        break;
      case "boolean":
        if (val === "") break;
        result = result.filter((row) =>
          val === "yes" ? Boolean(getFieldValue(row, field)) : !getFieldValue(row, field)
        );
        break;
      case "range": {
        if (!isRangeActive(val)) break;
        const min = val.min !== "" ? Number(val.min) : null;
        const max = val.max !== "" ? Number(val.max) : null;
        result = result.filter((row) => {
          const n = Number(getFieldValue(row, field));
          if (Number.isNaN(n)) return false;
          if (min != null && n < min) return false;
          if (max != null && n > max) return false;
          return true;
        });
        break;
      }
      case "dateRange": {
        if (!isDateRangeActive(val)) break;
        result = result.filter((row) => {
          const raw = getFieldValue(row, field);
          if (!raw) return false;
          const d = new Date(String(raw).slice(0, 10));
          if (Number.isNaN(d.getTime())) return false;
          if (val.from) {
            const from = new Date(val.from);
            if (d < from) return false;
          }
          if (val.to) {
            const to = new Date(val.to);
            to.setHours(23, 59, 59, 999);
            if (d > to) return false;
          }
          return true;
        });
        break;
      }
      default:
        break;
    }
  }

  return result;
}

function formatMetricValue(metric, value) {
  if (value == null || Number.isNaN(value)) return "—";
  switch (metric.format) {
    case "currency":
      return formatCurrency(Math.round(value));
    case "percent":
      return formatPercent(value);
    case "number":
      return Number(value).toLocaleString();
    default:
      return String(value);
  }
}

export function computeFilterMetrics(filtered, total, metricsConfig = []) {
  return metricsConfig.map((metric) => {
    switch (metric.type) {
      case "count":
        return {
          ...metric,
          value: filtered.length,
          display: filtered.length.toLocaleString(),
          sub: total.length !== filtered.length ? `of ${total.length.toLocaleString()} total` : null,
        };
      case "sum": {
        const sum = filtered.reduce((s, row) => s + (Number(row[metric.field]) || 0), 0);
        return {
          ...metric,
          value: sum,
          display: formatMetricValue(metric, sum),
        };
      }
      case "avg": {
        if (!filtered.length) {
          return { ...metric, value: 0, display: "—" };
        }
        const sum = filtered.reduce((s, row) => {
          const raw = metric.getValue ? metric.getValue(row) : row[metric.field];
          return s + (Number(raw) || 0);
        }, 0);
        const avg = sum / filtered.length;
        return {
          ...metric,
          value: avg,
          display: metric.format === "number" ? Math.round(avg).toLocaleString() : formatMetricValue(metric, avg),
        };
      }
      case "percent": {
        if (!total.length) {
          return { ...metric, value: 0, display: "0%" };
        }
        const pct = (filtered.length / total.length) * 100;
        return {
          ...metric,
          value: pct,
          display: formatPercent(pct),
        };
      }
      default:
        return { ...metric, display: "—" };
    }
  });
}

export function getActiveFilterChips(filters, config, fieldOptions) {
  const chips = [];

  if (filters.search?.trim()) {
    chips.push({ key: "search", label: `Search: "${filters.search.trim()}"` });
  }

  for (const field of config.fields ?? []) {
    const val = filters[field.key];
    const label = field.label;

    switch (field.type) {
      case "multi":
        if (val?.length) {
          chips.push({
            key: field.key,
            label: `${label}: ${val.length <= 2 ? val.join(", ") : `${val.length} selected`}`,
          });
        }
        break;
      case "range":
        if (isRangeActive(val)) {
          const parts = [];
          if (val.min !== "") parts.push(`≥ ${val.min}`);
          if (val.max !== "") parts.push(`≤ ${val.max}`);
          chips.push({ key: field.key, label: `${label}: ${parts.join(" · ")}` });
        }
        break;
      case "dateRange":
        if (isDateRangeActive(val)) {
          const parts = [];
          if (val.from) parts.push(`from ${val.from}`);
          if (val.to) parts.push(`to ${val.to}`);
          chips.push({ key: field.key, label: `${label}: ${parts.join(" ")}` });
        }
        break;
      default:
        if (val !== "" && val != null) {
          chips.push({ key: field.key, label: `${label}: ${val}` });
        }
    }
  }

  return chips;
}
